import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  useFloating,
  offset,
  flip,
  shift,
  arrow,
  autoUpdate,
  useDismiss,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import styled, { keyframes } from 'styled-components';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

const TooltipWrapper = styled.span`
  display: flex;
  align-items: center;
  position: relative;

  svg {
    width: 16px;
    height: 16px;
    fill: ${themeColors.colorGrey3};
    transition: fill 0.2s ease;
    margin-top: 2px;
  }
`;

const FloatingWrapper = styled.div`
  position: absolute;
  z-index: 1000;
`;

const TooltipBox = styled.div`
  background-color: ${themeColors.colorBackgroundSecondary};
  color: ${themeColors.colorTextSecondary};
  padding: ${pxToRem(12)};
  border-radius: ${variables.borderRadius.borderRadius};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 400;
  width: ${pxToRem(220)};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  animation: ${fadeIn} 0.15s ease forwards;
  display: flex;
  text-align: left;
  gap: 2px;

  svg {
    fill: ${themeColors.colorTextSecondary};
    width: 14px;
    margin-right: 2px;
  }
`;

const TooltipArrow = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: ${themeColors.colorBackgroundSecondary};
  transform: rotate(45deg);
`;

export const InfoTooltip = ({ content, placement = 'top', onClick }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const {
    refs,
    floatingStyles,
    context,
    placement: resolvedPlacement,
    middlewareData,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(8), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[resolvedPlacement.split('-')[0]];

  return (
    <>
      <TooltipWrapper ref={refs.setReference} {...getReferenceProps()}>
        <InfoIcon />
      </TooltipWrapper>

      {open && (
        <FloatingWrapper
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
          }}
          {...getFloatingProps()}
        >
          <TooltipBox>
            <InfoIcon onClick={onClick} />
            {content}
            <TooltipArrow
              ref={arrowRef}
              style={{
                left: middlewareData.arrow?.x ?? '',
                top: middlewareData.arrow?.y ?? '',
                [staticSide]: '-4px',
              }}
            />
          </TooltipBox>
        </FloatingWrapper>
      )}
    </>
  );
};

InfoTooltip.propTypes = {
  content: PropTypes.node,
  placement: PropTypes.string,
  onClick: PropTypes.func,
};
